import { useQuery } from '@tanstack/react-query';
import { queryDataSource, type QueryResponse } from '../../api/query';
import type { ParameterConfig } from '../../types/panelConfig';
import type { Donem } from '../satis-analiz/donem';
import { kategoriKayipSozlugu } from './kategori';

/** Bir sorgu kaynagi: ds id'si + bildirdigi parametreler (parametre gonderme karari buradan). */
export interface StokKaynak {
  sourceId: number;
  name: string;
  parameters: ParameterConfig[];
}

/**
 * R2 — `useStokFiltre.ts` de aynı klasörde `StokFiltre` adında bir arayüz export ediyor
 * (URL <-> filtre durumu). Bu ikisi AYNI ad çakışıyordu; sorgu tarafındakini yeniden adlandırdık.
 */
export interface StokSorguFiltresi {
  donem: Donem;
  sube: string | null;
  kategori: string | null;
  /** R1 — yalnız @TUR bildiren ds'lere gönderilir (990062/990063). Boş/undefined ise gönderilmez. */
  tur?: string | null;
}

/**
 * ds bildirmiyorsa parametre GÖNDERİLMEZ (Postgres 42883: bağlanmamış parametre).
 * Boş değer de gönderilmez — iki ayrı önbellek girdisi oluştururdu.
 * SA'nın `parametreBildiriyor`u burada kopyalandı: onun tip birliği '@KATEGORI'yi tanımıyor
 * ve SA dosyasını değiştirmemek için (asgari etki) üç satır tekrarlandı.
 *
 * Düzeltme turu 2/5, F1 kök-neden kapatması — EXPORT edildi: `detay/StokDetayModal.tsx`'in
 * `useDetaySorgu`su da (@STOK/@KOVA gibi `StokSorguFiltresi` dışı alanlar enjekte eden) AYNI
 * bildirim kapısından geçmeli — ds'in bildirmediği bir `ekstra` anahtarı sessizce backend'e kadar
 * gidip orada düşmesin (dünkü Critical'ın kök nedeni tam buydu: 990061'e `@TUR` enjekte edilmişti).
 */
export function bildiriyor(kaynak: StokKaynak, ad: string): boolean {
  return kaynak.parameters.some((p) => p.key.toUpperCase() === ad);
}

/**
 * ⚠ `@BASTARIH`/`@BITTARIH` de `bildiriyor()` ile kapılanır (SA'daki gibi koşulsuz DEĞİL):
 * 990043/990044/990050/990064/990023 tarih parametresi bildirmez; koşulsuz gönderilirse
 * Postgres 42883 verir (S2).
 *
 * R1 — canlı/tezgâh `data_sources`'tan doğrulandı (SQL 144, 2026-09-16): 990062 (Kayıp Ürün) `@TUR`
 * bildirir, 990063 (Kayıp Belge) `@TUR` + `@LOKASYON` bildirir. Diğer kayıp ds'leri (990059-990061,
 * 990065) `@TUR` bildirmez — koşulsuz gönderilirse yine 42883.
 */
export function stokParametreleri(kaynak: StokKaynak, f: StokSorguFiltresi): Record<string, string> {
  const p: Record<string, string> = {};
  if (bildiriyor(kaynak, '@BASTARIH')) p['@BASTARIH'] = f.donem.bas;
  if (bildiriyor(kaynak, '@BITTARIH')) p['@BITTARIH'] = f.donem.bit;
  if (f.sube && bildiriyor(kaynak, '@LOKASYON')) p['@LOKASYON'] = f.sube;
  if (f.kategori && bildiriyor(kaynak, '@KATEGORI')) p['@KATEGORI'] = kategoriKayipSozlugu(f.kategori);
  if (f.tur && bildiriyor(kaynak, '@TUR')) p['@TUR'] = f.tur;
  return p;
}

export function stokQueryKey(sourceId: number | undefined, parametreler: Record<string, string>) {
  return ['stok', sourceId, parametreler] as const;
}

export interface StokSorguSecenegi {
  onbellekten?: boolean;
}

export function useStokSorgu<T>(
  kaynak: StokKaynak | undefined,
  f: StokSorguFiltresi,
  enabled = true,
  secenek: StokSorguSecenegi = {},
) {
  const parametreler = kaynak ? stokParametreleri(kaynak, f) : {};
  return useQuery<QueryResponse<T>>({
    queryKey: stokQueryKey(kaynak?.sourceId, parametreler),
    queryFn: () => queryDataSource<T>({ sourceId: kaynak!.sourceId, parameters: parametreler }),
    enabled: !!kaynak && enabled,
    // Bir kaynak duserse ekran tumden cokmesin, o blok bos kalsin (SA ile ayni desen).
    throwOnError: false,
    ...(secenek.onbellekten ? { refetchOnMount: false } : {}),
  });
}
